import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useCurrencyStore } from '@/stores/useCurrencyStore'

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format currency with conversion support
 * Uses the currency store to convert and format amounts
 */
export function formatCurrency(value: number, sourceCurrency: string = 'USD'): string {
  // Guard clause: handle null, undefined, and NaN values
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00'
  }
  
  // Try to use currency store for conversion
  try {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: sourceCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    }

    // Get store state
    const store = useCurrencyStore.getState()
    const targetCurrency = store.currency
    
    // Convert from source currency to user's preferred currency
    const convertedValue = store.convert(value, sourceCurrency as any, targetCurrency)
    
    // Format using the store's formatter
    const formatted = store.formatCurrency(convertedValue, targetCurrency)
    
    return formatted
  } catch (error) {
    console.error('[formatCurrency] Error:', error)
    // Fallback to basic formatting if store is not available
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: sourceCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }
}

/**
 * Format percentage
 */
export function formatPercent(value: number, decimals: number = 2): string {
  // Guard clause: handle null, undefined, and NaN values
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%'
  }
  
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`
}

/**
 * Format date
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}

/**
 * Haptic feedback for mobile
 */
export function hapticFeedback(type: 'light' | 'medium' | 'heavy' = 'medium') {
  if ('vibrate' in navigator) {
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 50,
    }
    navigator.vibrate(patterns[type])
  }
}
